export type StatusTone = 'pos' | 'warn' | 'neg' | 'acc' | 'vio';

export interface LotLineage {
  id: string;
  line: string;
  type: string;
  source: string;
  released: string;
  study: string;
  units: number;
  doses: number;
  status: StatusTone;
  statusLbl: string;
  vsMean: string;
  respIndex: number;
  viability: string;
  passage: string;
  endotoxin: string;
  followup: number;
  koosDelta: string;
  custody: Array<[string, 'labs' | 'phy' | 'pat', string, string, string]>;
}

export interface ProviderSite {
  id: string;
  initials: string;
  name: string;
  org: string;
  type: string;
  region: string;
  lead: string;
  leadId: string;
  patients: number;
  doses: number;
  lots: string[];
  respIndex: number;
  status: StatusTone;
  statusLbl: string;
}

export interface StudyRecord {
  code: string;
  name: string;
  phase: string;
  status: StatusTone;
  statusLbl: string;
  enrolled: number;
  target: number;
  sites: number;
  pi: string;
  piId: string;
  started: string;
  desc: string;
  primary: string;
  secondary: string;
  lots: string[];
  cohorts: Array<[string, string, string, number, number, string, StatusTone]>;
}

export interface Investigator {
  id: string;
  initials: string;
  name: string;
  cred: string;
  specialty: string;
  role: 'Principal Investigator' | 'Sub-Investigator';
  npi: string;
  affiliation: string;
  region: string;
  email: string;
  since: string;
  status: StatusTone;
  statusLbl: string;
  focus: string;
  license: boolean;
  gcp: boolean;
  disclosure: boolean;
  delegation: boolean;
}

export interface SignalRecord {
  id: string;
  rank: number;
  title: string;
  driver: string;
  desc: string;
  conf: number;
  n: number;
  lot: string | null;
  study: string;
  detail: string;
  status?: 'candidate' | 'reviewed' | 'dismissed';
}

export interface PatientRecord {
  initials: string;
  name: string;
  pid: string;
  indication: string;
  zone: 'labs' | 'phy' | 'pat';
  lot: string;
  stageIdx: number;
  idx: number;
  status: StatusTone;
  d: string;
  sp: number[];
  age: number;
  sex: string;
  side: string;
  study: string;
  provider: string;
  enrolled: string;
  days: number;
}

export interface AdverseEventRecord {
  id: string;
  pid: string;
  lot: string;
  term: string;
  system: string;
  severity: 'Mild' | 'Moderate' | 'Severe';
  serious: boolean;
  expectedness: string;
  relatedness: string;
  outcome: string;
  status: 'New' | 'Under review' | 'Closed';
  onset: string;
  daysFromDose: number;
  reporter: string;
  reportable: boolean;
  due: string;
  narrative: string;
  actions: Array<[string, string, string]>;
  name?: string;
  initials?: string;
  study?: string;
  provider?: string;
  indication?: string;
}

export interface AuditEntryRecord {
  id: string;
  at: string;
  actor: string;
  role: string;
  initials: string;
  verb: string;
  target: string;
  kind: 'release' | 'access' | 'consent' | 'export' | 'ae' | 'enroll' | 'edit' | 'auth' | 'sign';
  status: 'ok' | 'flag' | 'pending';
  go?: string;
}

export interface MarkerRecord {
  name: string;
  sub: string;
  value: string;
  refText: string;
  finding: 'pass' | 'warn' | 'fail' | 'unknown';
  prov: 'measured' | 'imported' | 'reference';
}

export interface QcPanel {
  lotId: string;
  risk: 'low' | 'med' | 'high';
  confidence: number;
  summary: string;
  reviewedBasis: string;
  markers: MarkerRecord[];
  recs: string[];
}

export const STAGES = ['Source', 'Produce', 'Acquire', 'Administer', 'Measure', 'Learn'] as const;

export const zoneColors = {
  labs: 'var(--labs)',
  phy: 'var(--phy)',
  pat: 'var(--pat)',
};

export const lots: LotLineage[] = [
  {
    id: '4471',
    line: 'RGL-12',
    type: 'Allogeneic MSC',
    source: 'Umbilical',
    released: 'Mar 28, 2026',
    study: 'KOA-3',
    units: 8,
    doses: 64,
    status: 'pos',
    statusLbl: 'chain-of-custody verified',
    vsMean: '+18%',
    respIndex: 88,
    viability: '96.4%',
    passage: 'P3',
    endotoxin: '<0.5 EU',
    followup: 96,
    koosDelta: '+18',
    custody: [
      ['source', 'labs', 'Sourced', 'Donor consent and eligibility verified under SOP-114.', 'Mar 14'],
      ['flask', 'labs', 'Characterized', 'Viability 96.4%, passage P3, identity and sterility panel passed.', 'Mar 20'],
      ['produce', 'labs', 'Released as Lot 4471', '8 units, QC released, cryopreserved per SOP-208.', 'Mar 28'],
      ['acquire', 'phy', 'Distributed', 'Northgate Ortho (6), Westside Sports Med (2).', 'Apr 01'],
      ['admin', 'phy', 'Administered', '64 doses logged across 4 providers, all unit-traced.', 'Apr 02-May 30'],
      ['measure', 'pat', 'Outcomes linked', '64 patients in active follow-up; candidate signal surfaced.', 'ongoing'],
    ],
  },
  {
    id: '4482',
    line: 'RGL-12',
    type: 'Allogeneic MSC',
    source: 'Umbilical',
    released: 'Apr 11, 2026',
    study: 'KOA-3',
    units: 6,
    doses: 48,
    status: 'acc',
    statusLbl: 'in follow-up',
    vsMean: '-4%',
    respIndex: 71,
    viability: '94.1%',
    passage: 'P4',
    endotoxin: '<0.5 EU',
    followup: 92,
    koosDelta: '+12',
    custody: [
      ['source', 'labs', 'Sourced', 'Donor consent and eligibility verified under SOP-114.', 'Mar 30'],
      ['flask', 'labs', 'Characterized', 'Viability 94.1%, passage P4, identity panel passed.', 'Apr 04'],
      ['produce', 'labs', 'Released as Lot 4482', '6 units, QC released.', 'Apr 11'],
      ['acquire', 'phy', 'Distributed', 'Lakeside Joint and Spine (4), Westside Sports Med (2).', 'Apr 14'],
      ['admin', 'phy', 'Administered', '48 doses logged across 3 providers.', 'Apr 15-May 28'],
      ['measure', 'pat', 'Outcomes linked', '48 patients in follow-up; plateau flagged for review.', 'ongoing'],
    ],
  },
  {
    id: '4468',
    line: 'RGL-09',
    type: 'Allogeneic MSC',
    source: 'Cord tissue',
    released: 'Mar 12, 2026',
    study: 'RCR-2',
    units: 7,
    doses: 52,
    status: 'pos',
    statusLbl: 'chain-of-custody verified',
    vsMean: '+6%',
    respIndex: 79,
    viability: '95.2%',
    passage: 'P3',
    endotoxin: '<0.5 EU',
    followup: 94,
    koosDelta: '+15',
    custody: [
      ['source', 'labs', 'Sourced', 'Donor eligibility verified under SOP-114.', 'Feb 26'],
      ['flask', 'labs', 'Characterized', 'Viability 95.2%, passage P3, sterility passed.', 'Mar 03'],
      ['produce', 'labs', 'Released as Lot 4468', '7 units, QC released.', 'Mar 12'],
      ['acquire', 'phy', 'Distributed', 'Northgate Ortho (4), Cedar Sports (3).', 'Mar 15'],
      ['admin', 'phy', 'Administered', '52 doses across 3 providers.', 'Mar 16-May 12'],
      ['measure', 'pat', 'Outcomes linked', '52 patients in follow-up.', 'ongoing'],
    ],
  },
  {
    id: '4490',
    line: 'RGL-14',
    type: 'Autologous BMAC',
    source: 'Bone marrow',
    released: 'Apr 25, 2026',
    study: 'CDR-1',
    units: 5,
    doses: 30,
    status: 'acc',
    statusLbl: 'in follow-up',
    vsMean: '+2%',
    respIndex: 74,
    viability: '97.0%',
    passage: 'P2',
    endotoxin: '<0.5 EU',
    followup: 90,
    koosDelta: '+11',
    custody: [
      ['source', 'labs', 'Sourced', 'Autologous draw, same-day processing.', 'Apr 22'],
      ['flask', 'labs', 'Characterized', 'Viability 97.0%, passage P2.', 'Apr 24'],
      ['produce', 'labs', 'Released as Lot 4490', '5 units, QC released.', 'Apr 25'],
      ['acquire', 'phy', 'Distributed', 'Lakeside Joint and Spine (5).', 'Apr 26'],
      ['admin', 'phy', 'Administered', '30 doses logged.', 'Apr 27-May 30'],
      ['measure', 'pat', 'Outcomes linked', '30 patients in early follow-up.', 'ongoing'],
    ],
  },
  {
    id: '4455',
    line: 'RGL-09',
    type: 'Allogeneic MSC',
    source: 'Cord tissue',
    released: 'Feb 20, 2026',
    study: 'TND-1',
    units: 6,
    doses: 41,
    status: 'warn',
    statusLbl: 'variability review',
    vsMean: '-9%',
    respIndex: 62,
    viability: '92.8%',
    passage: 'P4',
    endotoxin: '<0.7 EU',
    followup: 88,
    koosDelta: '+7',
    custody: [
      ['source', 'labs', 'Sourced', 'Donor eligibility verified.', 'Feb 04'],
      ['flask', 'labs', 'Characterized', 'Viability 92.8%, passage P4, flagged for review.', 'Feb 10'],
      ['produce', 'labs', 'Released as Lot 4455', '6 units, QC released.', 'Feb 20'],
      ['acquire', 'phy', 'Distributed', 'Cedar Sports (6).', 'Feb 23'],
      ['admin', 'phy', 'Administered', '41 doses logged.', 'Feb 24-Apr 30'],
      ['measure', 'pat', 'Outcomes linked', '41 patients; lower outcome pattern flagged.', 'ongoing'],
    ],
  },
  {
    id: '4501',
    line: 'RGL-14',
    type: 'Autologous BMAC',
    source: 'Bone marrow',
    released: 'May 09, 2026',
    study: 'KOA-3',
    units: 4,
    doses: 12,
    status: 'acc',
    statusLbl: 'early follow-up',
    vsMean: '-',
    respIndex: 0,
    viability: '96.9%',
    passage: 'P2',
    endotoxin: '<0.5 EU',
    followup: 0,
    koosDelta: '-',
    custody: [
      ['source', 'labs', 'Sourced', 'Autologous draw.', 'May 06'],
      ['flask', 'labs', 'Characterized', 'Viability 96.9%, passage P2.', 'May 08'],
      ['produce', 'labs', 'Released as Lot 4501', '4 units, QC released.', 'May 09'],
      ['acquire', 'phy', 'Distributed', 'Northgate Ortho (4).', 'May 10'],
      ['admin', 'phy', 'Administered', '12 doses logged so far.', 'May 12-present'],
      ['measure', 'pat', 'Outcomes linked', '12 patients enrolled, baseline collected.', 'ongoing'],
    ],
  },
];

export const providers: ProviderSite[] = [
  { id: 'northgate', initials: 'NO', name: 'Northgate Ortho', org: 'Northgate Regenerative Institute', type: 'Orthopedic', region: 'Northeast', lead: 'Dr. Elena Reyes', leadId: 'reyes', patients: 38, doses: 142, lots: ['4471', '4468', '4501'], respIndex: 86, status: 'pos', statusLbl: 'top reviewed pattern' },
  { id: 'westside', initials: 'WS', name: 'Westside Sports Med', org: 'Westside Sports Medicine', type: 'Sports medicine', region: 'West', lead: 'Dr. Priya Nair', leadId: 'nair', patients: 24, doses: 88, lots: ['4471', '4482'], respIndex: 79, status: 'pos', statusLbl: 'on track' },
  { id: 'lakeside', initials: 'LJ', name: 'Lakeside Joint and Spine', org: 'Lakeside Joint and Spine Center', type: 'Orthopedic', region: 'Midwest', lead: 'Dr. Tomas Vega', leadId: 'vega', patients: 21, doses: 78, lots: ['4482', '4490'], respIndex: 73, status: 'acc', statusLbl: 'in follow-up' },
  { id: 'cedar', initials: 'CS', name: 'Cedar Sports', org: 'Cedar Sports Orthopedics', type: 'Sports medicine', region: 'South', lead: 'Dr. Hannah Ross', leadId: 'ross', patients: 19, doses: 93, lots: ['4468', '4455'], respIndex: 68, status: 'warn', statusLbl: 'variability review' },
  { id: 'summit', initials: 'SR', name: 'Summit Regen', org: 'Summit Regenerative Health', type: 'Regenerative', region: 'West', lead: 'Dr. Omar Haddad', leadId: 'haddad', patients: 16, doses: 54, lots: ['4471'], respIndex: 84, status: 'pos', statusLbl: 'on track' },
  { id: 'harbor', initials: 'HB', name: 'Harbor Bone and Joint', org: 'Harbor Bone and Joint Clinic', type: 'Orthopedic', region: 'Northeast', lead: 'Dr. Lia Moreno', leadId: 'moreno', patients: 14, doses: 47, lots: ['4490'], respIndex: 71, status: 'acc', statusLbl: 'in follow-up' },
];

export const studies: StudyRecord[] = [
  {
    code: 'KOA-3',
    name: 'Knee OA Outcomes',
    phase: 'Observational',
    status: 'pos',
    statusLbl: 'Enrolling',
    enrolled: 412,
    target: 450,
    sites: 6,
    pi: 'Dr. Mara Okonkwo',
    piId: 'okonkwo',
    started: 'Jan 12, 2026',
    desc: 'Prospective registry tracking 6-month patient-reported outcomes across allogeneic MSC and autologous BMAC product lines in knee osteoarthritis.',
    primary: 'KOOS change at 6 months',
    secondary: 'Pain (VAS), function, adverse events',
    lots: ['4471', '4482', '4501'],
    cohorts: [
      ['Cohort A', 'Mild-moderate OA', 'var(--accent)', 128, 130, 'On track', 'pos'],
      ['Cohort B', 'Moderate OA', 'var(--phy)', 116, 120, 'On track', 'pos'],
      ['Cohort C', 'Severe OA', 'var(--pat)', 96, 110, 'Enrolling', 'acc'],
      ['Cohort D', 'Bilateral OA', 'var(--violet)', 72, 90, 'Behind', 'warn'],
    ],
  },
  {
    code: 'RCR-2',
    name: 'Rotator Cuff Repair',
    phase: 'Registry',
    status: 'pos',
    statusLbl: 'Enrolling',
    enrolled: 286,
    target: 300,
    sites: 4,
    pi: 'Dr. Samuel Pratt',
    piId: 'pratt',
    started: 'Feb 02, 2026',
    desc: 'Registry of rotator cuff repair augmentation with allogeneic MSC injection, measuring functional recovery and re-tear rate.',
    primary: 'ASES score at 6 months',
    secondary: 'Re-tear rate, range of motion',
    lots: ['4468'],
    cohorts: [
      ['Cohort A', 'Partial tear', 'var(--accent)', 142, 150, 'On track', 'pos'],
      ['Cohort B', 'Full tear', 'var(--phy)', 98, 100, 'On track', 'pos'],
      ['Cohort C', 'Revision', 'var(--pat)', 46, 50, 'Enrolling', 'acc'],
    ],
  },
  {
    code: 'CDR-1',
    name: 'Cartilage Defect Registry',
    phase: 'Observational',
    status: 'acc',
    statusLbl: 'Follow-up',
    enrolled: 198,
    target: 200,
    sites: 3,
    pi: 'Dr. Mara Okonkwo',
    piId: 'okonkwo',
    started: 'Nov 18, 2025',
    desc: 'Observational study of focal cartilage defect repair using autologous BMAC, tracking MRI cartilage fill and functional scores.',
    primary: 'MOCART score at 12 months',
    secondary: 'IKDC, return to activity',
    lots: ['4490'],
    cohorts: [
      ['Cohort A', 'Femoral condyle', 'var(--accent)', 118, 120, 'Follow-up', 'acc'],
      ['Cohort B', 'Patellar', 'var(--phy)', 80, 80, 'Complete', 'pos'],
    ],
  },
  {
    code: 'TND-1',
    name: 'Tendinopathy Cohort',
    phase: 'Registry',
    status: 'acc',
    statusLbl: 'Follow-up',
    enrolled: 164,
    target: 180,
    sites: 2,
    pi: 'Dr. Samuel Pratt',
    piId: 'pratt',
    started: 'Dec 04, 2025',
    desc: 'Registry of chronic tendinopathy cases using allogeneic MSC, measuring pain and return-to-sport timelines.',
    primary: 'VISA score at 6 months',
    secondary: 'Return to sport, pain (VAS)',
    lots: ['4455'],
    cohorts: [
      ['Cohort A', 'Achilles', 'var(--accent)', 92, 100, 'Follow-up', 'acc'],
      ['Cohort B', 'Patellar tendon', 'var(--phy)', 72, 80, 'Enrolling', 'acc'],
    ],
  },
  {
    code: 'LVS-1',
    name: 'Lot Variability Study',
    phase: 'Analysis',
    status: 'acc',
    statusLbl: 'Active',
    enrolled: 224,
    target: 240,
    sites: 5,
    pi: 'Dr. Mara Okonkwo',
    piId: 'okonkwo',
    started: 'Mar 01, 2026',
    desc: 'Cross-study analysis isolating product-line and lot-level variables driving outcome differences.',
    primary: 'Response index variance by lot',
    secondary: 'Viability correlation, passage effect',
    lots: ['4471', '4482', '4455', '4468'],
    cohorts: [
      ['Group RGL-12', 'High viability', 'var(--accent)', 96, 100, 'Active', 'acc'],
      ['Group RGL-09', 'Mixed', 'var(--phy)', 84, 90, 'Active', 'acc'],
      ['Group RGL-14', 'Autologous', 'var(--pat)', 44, 50, 'Active', 'acc'],
    ],
  },
  {
    code: 'LTS-1',
    name: 'Long-term Safety',
    phase: 'Registry',
    status: 'warn',
    statusLbl: 'Setup',
    enrolled: 0,
    target: 500,
    sites: 6,
    pi: 'Dr. Lia Moreno',
    piId: 'moreno',
    started: 'Jun 01, 2026',
    desc: 'Five-year safety registry across all product lines, capturing long-term adverse events and durability of response.',
    primary: 'Serious adverse event rate at 5 years',
    secondary: 'Durability of response, repeat follow-up',
    lots: [],
    cohorts: [['All lines', 'Pooled safety', 'var(--faint)', 0, 500, 'Setup', 'warn']],
  },
];

export const investigators: Investigator[] = [
  { id: 'okonkwo', initials: 'MO', name: 'Dr. Mara Okonkwo', cred: 'MD, PhD', specialty: 'Orthopedic surgery', role: 'Principal Investigator', npi: '1740293845', affiliation: 'northgate', region: 'Northeast', email: 'm.okonkwo@northgateri.org', since: 'Jan 8, 2026', status: 'pos', statusLbl: 'lead PI', focus: 'Knee OA, cartilage repair', license: true, gcp: true, disclosure: true, delegation: true },
  { id: 'pratt', initials: 'SP', name: 'Dr. Samuel Pratt', cred: 'MD', specialty: 'Sports medicine', role: 'Principal Investigator', npi: '1558204417', affiliation: 'westside', region: 'West', email: 's.pratt@westsidesm.org', since: 'Jan 22, 2026', status: 'pos', statusLbl: 'active PI', focus: 'Rotator cuff, tendinopathy', license: true, gcp: true, disclosure: true, delegation: true },
  { id: 'moreno', initials: 'LM', name: 'Dr. Lia Moreno', cred: 'MD', specialty: 'Orthopedic surgery', role: 'Principal Investigator', npi: '1629930012', affiliation: 'harbor', region: 'Northeast', email: 'l.moreno@harborbj.org', since: 'Mar 2, 2026', status: 'acc', statusLbl: 'study in setup', focus: 'Long-term safety', license: true, gcp: true, disclosure: true, delegation: false },
  { id: 'reyes', initials: 'ER', name: 'Dr. Elena Reyes', cred: 'MD', specialty: 'Orthopedic surgery', role: 'Sub-Investigator', npi: '1093847562', affiliation: 'northgate', region: 'Northeast', email: 'e.reyes@northgateortho.org', since: 'Feb 1, 2026', status: 'pos', statusLbl: 'site lead', focus: 'Knee OA delivery', license: true, gcp: true, disclosure: true, delegation: true },
  { id: 'nair', initials: 'PN', name: 'Dr. Priya Nair', cred: 'MD', specialty: 'Sports medicine', role: 'Sub-Investigator', npi: '1384756203', affiliation: 'westside', region: 'West', email: 'p.nair@westsidesm.org', since: 'Feb 5, 2026', status: 'pos', statusLbl: 'site lead', focus: 'Sports injury', license: true, gcp: true, disclosure: true, delegation: true },
  { id: 'vega', initials: 'TV', name: 'Dr. Tomas Vega', cred: 'MD', specialty: 'Orthopedic surgery', role: 'Sub-Investigator', npi: '1209384756', affiliation: 'lakeside', region: 'Midwest', email: 't.vega@lakesidejs.org', since: 'Feb 11, 2026', status: 'acc', statusLbl: 'site lead', focus: 'Joint and spine', license: true, gcp: true, disclosure: false, delegation: true },
  { id: 'ross', initials: 'HR', name: 'Dr. Hannah Ross', cred: 'MD', specialty: 'Sports medicine', role: 'Sub-Investigator', npi: '1473625190', affiliation: 'cedar', region: 'South', email: 'h.ross@cedarsports.org', since: 'Feb 18, 2026', status: 'warn', statusLbl: 'variability review', focus: 'Tendinopathy', license: true, gcp: true, disclosure: true, delegation: true },
  { id: 'haddad', initials: 'OH', name: 'Dr. Omar Haddad', cred: 'MD', specialty: 'Regenerative medicine', role: 'Sub-Investigator', npi: '1562738491', affiliation: 'summit', region: 'West', email: 'o.haddad@summitregen.org', since: 'Feb 24, 2026', status: 'pos', statusLbl: 'site lead', focus: 'Regenerative care', license: true, gcp: true, disclosure: true, delegation: true },
];

export const signals: SignalRecord[] = [
  { id: 's1', rank: 1, title: 'Lot 4471 candidate outcome pattern', driver: 'Lot / product line', desc: 'Allogeneic line RGL-12 is associated with a higher 6-month response pattern than the study mean in this reviewed cohort.', conf: 91, n: 64, lot: '4471', study: 'KOA-3', detail: 'Across the KOA-3 cohort, patients dosed from Lot 4471 reached a mean response index of 88 versus a study mean of 74. This is a candidate signal that requires qualified review before any interpretation.' },
  { id: 's2', rank: 2, title: 'Earlier administration trajectory pattern', driver: 'Time to procedure', desc: 'Patients with procedures within 6 weeks of referral show a steeper week-12 trajectory in the registry data.', conf: 84, n: 318, lot: null, study: 'KOA-3', detail: 'Time from referral to first dose is inversely associated with week-12 recovery slope. This outcome pattern is confounded by access timing and indication mix.' },
  { id: 's3', rank: 3, title: 'Cartilage-defect subgroup above modeled curve', driver: 'Indication severity', desc: 'Cartilage-defect cohort is tracking above the modeled curve since week 8.', conf: 79, n: 112, lot: null, study: 'CDR-1', detail: 'The cartilage-defect subgroup is above the modeled curve from week 8 onward. This is an outcome pattern for review, not a care recommendation.' },
  { id: 's4', rank: 4, title: 'Lot 4482 plateau after month 4', driver: 'Lot / product line', desc: 'Response gains flatten earlier; flagged for product-variable review.', conf: 72, n: 58, lot: '4482', study: 'KOA-3', detail: 'Lot 4482 shows comparable early gains but plateaus around month 4, ending below study mean. Flagged for product-variable review against passage number and viability at release.' },
  { id: 's5', rank: 5, title: 'Cohort D follow-up gap', driver: 'Protocol adherence', desc: '14 patients are overdue for 90-day check-in; reminders re-queued automatically.', conf: 68, n: 14, lot: null, study: 'KOA-3', detail: 'Cohort D is showing reduced follow-up adherence, with 14 patients overdue for their 90-day check-in. Site coordinators have been notified.' },
  { id: 's6', rank: 6, title: 'Viability association needs review', driver: 'Lot / product line', desc: 'Lots released above 95% viability show a modest association with higher response index.', conf: 64, n: 246, lot: null, study: 'LVS-1', detail: 'Pooling across studies, lots released with viability above 95% are associated with a modest response-index difference. The relationship is confounded with passage number and isolated in LVS-1 for review.' },
];

export const patients: PatientRecord[] = [
  { initials: 'AR', name: 'Amara Reyes', pid: 'P-2048', indication: 'Knee OA', zone: 'pat', lot: '4471', stageIdx: 4, idx: 88, status: 'pos', d: '+21', sp: [0.3, 0.4, 0.55, 0.7, 0.82, 0.9], age: 54, sex: 'F', side: 'R', study: 'KOA-3', provider: 'northgate', enrolled: 'Apr 9, 2026', days: 182 },
  { initials: 'JC', name: 'James Carter', pid: 'P-2041', indication: 'Rotator cuff', zone: 'pat', lot: '4468', stageIdx: 4, idx: 74, status: 'pos', d: '+17', sp: [0.3, 0.38, 0.5, 0.6, 0.68, 0.74], age: 61, sex: 'M', side: 'L', study: 'RCR-2', provider: 'cedar', enrolled: 'Apr 2, 2026', days: 174 },
  { initials: 'LW', name: 'Lena Wu', pid: 'P-2039', indication: 'Cartilage defect', zone: 'pat', lot: '4490', stageIdx: 4, idx: 61, status: 'warn', d: '+9', sp: [0.35, 0.4, 0.46, 0.52, 0.57, 0.61], age: 43, sex: 'F', side: 'R', study: 'CDR-1', provider: 'lakeside', enrolled: 'Mar 30, 2026', days: 168 },
  { initials: 'MO', name: 'Marco Oliveira', pid: 'P-2033', indication: 'Knee OA', zone: 'pat', lot: '4471', stageIdx: 3, idx: 80, status: 'pos', d: '+19', sp: [0.28, 0.36, 0.5, 0.64, 0.74, 0.8], age: 58, sex: 'M', side: 'L', study: 'KOA-3', provider: 'northgate', enrolled: 'Mar 24, 2026', days: 160 },
  { initials: 'SH', name: 'Sofia Haddad', pid: 'P-2030', indication: 'Tendinopathy', zone: 'pat', lot: '4455', stageIdx: 5, idx: 55, status: 'warn', d: '+6', sp: [0.4, 0.43, 0.47, 0.5, 0.53, 0.55], age: 37, sex: 'F', side: 'R', study: 'TND-1', provider: 'cedar', enrolled: 'Mar 20, 2026', days: 156 },
  { initials: 'DK', name: 'David Kim', pid: 'P-2025', indication: 'Cartilage defect', zone: 'pat', lot: '4490', stageIdx: 4, idx: 92, status: 'pos', d: '+26', sp: [0.25, 0.38, 0.55, 0.72, 0.85, 0.92], age: 47, sex: 'M', side: 'R', study: 'CDR-1', provider: 'lakeside', enrolled: 'Mar 15, 2026', days: 150 },
  { initials: 'NB', name: 'Nadia Brooks', pid: 'P-2019', indication: 'Knee OA', zone: 'pat', lot: '4482', stageIdx: 4, idx: 48, status: 'neg', d: '+2', sp: [0.42, 0.43, 0.45, 0.46, 0.47, 0.48], age: 66, sex: 'F', side: 'L', study: 'KOA-3', provider: 'westside', enrolled: 'Mar 5, 2026', days: 140 },
  { initials: 'TF', name: 'Theo Fischer', pid: 'P-2014', indication: 'Rotator cuff', zone: 'pat', lot: '4468', stageIdx: 3, idx: 77, status: 'pos', d: '+16', sp: [0.3, 0.37, 0.48, 0.6, 0.7, 0.77], age: 52, sex: 'M', side: 'R', study: 'RCR-2', provider: 'northgate', enrolled: 'Feb 28, 2026', days: 134 },
  { initials: 'IG', name: 'Iris Gallo', pid: 'P-2008', indication: 'Tendinopathy', zone: 'pat', lot: '4455', stageIdx: 4, idx: 83, status: 'pos', d: '+20', sp: [0.29, 0.39, 0.52, 0.66, 0.76, 0.83], age: 41, sex: 'F', side: 'L', study: 'TND-1', provider: 'cedar', enrolled: 'Feb 20, 2026', days: 128 },
  { initials: 'RP', name: 'Ravi Patel', pid: 'P-2002', indication: 'Knee OA', zone: 'pat', lot: '4471', stageIdx: 4, idx: 85, status: 'pos', d: '+22', sp: [0.27, 0.37, 0.52, 0.67, 0.78, 0.85], age: 59, sex: 'M', side: 'R', study: 'KOA-3', provider: 'summit', enrolled: 'Feb 14, 2026', days: 122 },
  { initials: 'EM', name: 'Elena Marsh', pid: 'P-1996', indication: 'Cartilage defect', zone: 'pat', lot: '4490', stageIdx: 4, idx: 69, status: 'warn', d: '+12', sp: [0.33, 0.4, 0.48, 0.56, 0.63, 0.69], age: 45, sex: 'F', side: 'L', study: 'CDR-1', provider: 'harbor', enrolled: 'Feb 8, 2026', days: 116 },
  { initials: 'OD', name: 'Owen Diaz', pid: 'P-1991', indication: 'Knee OA', zone: 'pat', lot: '4482', stageIdx: 5, idx: 76, status: 'pos', d: '+18', sp: [0.3, 0.38, 0.5, 0.61, 0.69, 0.76], age: 63, sex: 'M', side: 'L', study: 'KOA-3', provider: 'westside', enrolled: 'Feb 2, 2026', days: 110 },
];

function withPatient(ae: AdverseEventRecord): AdverseEventRecord {
  const patient = patients.find((p) => p.pid === ae.pid);
  return {
    ...ae,
    name: patient?.name ?? ae.pid,
    initials: patient?.initials ?? '--',
    study: patient?.study,
    provider: patient?.provider,
    indication: patient?.indication,
  };
}

export const adverseEvents: AdverseEventRecord[] = [
  withPatient({ id: 'AE-0021', pid: 'P-1996', lot: '4490', term: 'Septic arthritis - knee', system: 'Infections', severity: 'Severe', serious: true, expectedness: 'Unexpected', relatedness: 'Possible', outcome: 'Recovering', status: 'Under review', onset: 'May 29, 2026', daysFromDose: 9, reporter: 'Dr. Lia Moreno', reportable: true, due: 'Jun 13, 2026', narrative: 'Patient presented to ED 9 days post-injection with a hot, swollen knee, fever 38.6 C and raised inflammatory markers. Product lot quarantined pending review; sterility records re-examined.', actions: [['May 29', 'Dr. Lia Moreno', 'Event reported from Harbor Bone and Joint'], ['May 29', 'System', 'Lot #4490 flagged; sterility panel re-pulled and passed at release'], ['May 30', 'Dr. Mara Okonkwo', 'Classified Serious; expedited 15-day report opened'], ['Jun 02', 'Safety desk', 'Causality assessed Possible; continued review']] }),
  withPatient({ id: 'AE-0023', pid: 'P-2030', lot: '4455', term: 'Increased tendon pain', system: 'Musculoskeletal', severity: 'Moderate', serious: false, expectedness: 'Expected', relatedness: 'Probable', outcome: 'Recovering', status: 'Under review', onset: 'May 24, 2026', daysFromDose: 3, reporter: 'Dr. Hannah Ross', reportable: false, due: '', narrative: 'Transient worsening of Achilles pain for about 6 days after the second injection in the series, settling with rest and NSAIDs.', actions: [['May 24', 'Dr. Hannah Ross', 'Event reported during 14-day safety check'], ['May 26', 'Dr. Samuel Pratt', 'Reviewed expected flare; monitoring continued']] }),
  withPatient({ id: 'AE-0017', pid: 'P-1991', lot: '4482', term: 'Allergic rash', system: 'Skin', severity: 'Moderate', serious: false, expectedness: 'Unexpected', relatedness: 'Possible', outcome: 'Recovering', status: 'New', onset: 'Jun 06, 2026', daysFromDose: 1, reporter: 'Dr. Priya Nair', reportable: false, due: '', narrative: 'Pruritic maculopapular rash over the injected limb the day after procedure. No respiratory involvement. Resolving with oral antihistamine.', actions: [['Jun 06', 'Dr. Priya Nair', 'Event reported from Westside Sports Med']] }),
  withPatient({ id: 'AE-0024', pid: 'P-2019', lot: '4482', term: 'Injection-site swelling', system: 'General', severity: 'Mild', serious: false, expectedness: 'Expected', relatedness: 'Possible', outcome: 'Recovered', status: 'Closed', onset: 'May 12, 2026', daysFromDose: 2, reporter: 'Dr. Priya Nair', reportable: false, due: '', narrative: 'Localized swelling and warmth at the injection site for 4 days, resolved without intervention.', actions: [['May 12', 'Dr. Priya Nair', 'Event reported'], ['May 18', 'Dr. Samuel Pratt', 'Reviewed and closed']] }),
  withPatient({ id: 'AE-0019', pid: 'P-2008', lot: '4455', term: 'Vasovagal syncope', system: 'Nervous system', severity: 'Moderate', serious: false, expectedness: 'Expected', relatedness: 'Related', outcome: 'Recovered', status: 'Closed', onset: 'Apr 22, 2026', daysFromDose: 0, reporter: 'Dr. Hannah Ross', reportable: false, due: '', narrative: 'Brief vasovagal episode during the procedure; recovered fully within minutes in the supine position.', actions: [['Apr 22', 'Dr. Hannah Ross', 'Event reported at point-of-care'], ['Apr 24', 'Dr. Samuel Pratt', 'Reviewed and closed']] }),
];

export const auditEntries: AuditEntryRecord[] = [
  { id: 'a1', at: 'Today 9:35 AM', actor: 'Dr. Mara Okonkwo', role: 'Principal Investigator', initials: 'MO', verb: 'Viewed patient record', target: 'P-1996 - Elena Marsh', kind: 'access', status: 'ok', go: 'patient/P-1996' },
  { id: 'a2', at: 'Today 9:27 AM', actor: 'Dr. Lia Moreno', role: 'Principal Investigator', initials: 'LM', verb: 'Filed adverse event', target: 'AE-0021 - Septic arthritis', kind: 'ae', status: 'flag', go: 'ae/AE-0021' },
  { id: 'a3', at: 'Today 9:19 AM', actor: 'Dr. Elena Reyes', role: 'Research Coordinator', initials: 'ER', verb: 'Logged care episode', target: 'P-2048 - Lot #4471', kind: 'edit', status: 'ok', go: 'patient/P-2048' },
  { id: 'a4', at: 'Today 9:03 AM', actor: 'Priya Okafor', role: 'QA / Lot Release', initials: 'PO', verb: 'Released lot', target: 'Lot #4501 - RGL-14', kind: 'release', status: 'ok', go: 'lot/4501' },
  { id: 'a5', at: 'Today 8:49 AM', actor: 'Dr. Mara Okonkwo', role: 'Principal Investigator', initials: 'MO', verb: 'Exported registry', target: '1,284 rows - de-identified CSV', kind: 'export', status: 'ok' },
  { id: 'a6', at: 'Today 8:31 AM', actor: 'Dr. Priya Nair', role: 'Research Coordinator', initials: 'PN', verb: 'Filed adverse event', target: 'AE-0017 - Allergic rash', kind: 'ae', status: 'ok', go: 'ae/AE-0017' },
  { id: 'a7', at: 'Today 8:05 AM', actor: 'System', role: 'Automation', initials: 'SY', verb: 'Re-queued follow-up reminders', target: 'Cohort D - 14 patients', kind: 'edit', status: 'ok' },
  { id: 'a8', at: 'Yesterday 4:42 PM', actor: 'Dr. Samuel Pratt', role: 'Principal Investigator', initials: 'SP', verb: 'Signed off data', target: 'RCR-2 - Cohort A interim', kind: 'sign', status: 'ok', go: 'study/RCR-2' },
  { id: 'a9', at: 'Yesterday 2:15 PM', actor: 'System', role: 'Automation', initials: 'SY', verb: 'Surfaced candidate signal', target: 'Signal #1 - Lot 4471 outcome pattern', kind: 'edit', status: 'ok', go: 'signal/s1' },
  { id: 'a10', at: 'Jun 6 11:44 AM', actor: 'Dr. Hannah Ross', role: 'Research Coordinator', initials: 'HR', verb: 'Updated consent scope', target: 'P-2030 - repeat follow-up contact', kind: 'consent', status: 'ok', go: 'patient/P-2030' },
  { id: 'a11', at: 'Jun 6 8:12 AM', actor: 'Dr. Samuel Pratt', role: 'Principal Investigator', initials: 'SP', verb: 'Signed in', target: 'Password + 2FA - Westside', kind: 'auth', status: 'ok' },
];

export const qcPanels: QcPanel[] = [
  {
    lotId: '4471',
    risk: 'low',
    confidence: 0.91,
    summary: 'Release panel is internally consistent. The review queue should confirm identity, sterility, and viability evidence before operational use.',
    reviewedBasis: 'Measured QC markers, imported release record, citable reference ranges.',
    markers: [
      { name: 'Viability', sub: 'release', value: '96.4%', refText: '>= 85%', finding: 'pass', prov: 'measured' },
      { name: 'CD73', sub: 'identity', value: '98%', refText: '>= 95%', finding: 'pass', prov: 'measured' },
      { name: 'CD90', sub: 'identity', value: '97%', refText: '>= 95%', finding: 'pass', prov: 'measured' },
      { name: 'Endotoxin', sub: 'safety', value: '<0.5 EU', refText: '<0.5 EU', finding: 'pass', prov: 'reference' },
    ],
    recs: ['Document reviewer confirmation.', 'Keep outcome interpretation in signal-review workflow.'],
  },
  {
    lotId: '4482',
    risk: 'med',
    confidence: 0.78,
    summary: 'Passage and plateau pattern should be reviewed together. This is a review prompt, not a status change.',
    reviewedBasis: 'QC markers, passage record, outcome pattern preview.',
    markers: [
      { name: 'Viability', sub: 'release', value: '94.1%', refText: '>= 85%', finding: 'pass', prov: 'measured' },
      { name: 'Passage', sub: 'expansion', value: 'P4', refText: 'P2-P4', finding: 'warn', prov: 'imported' },
      { name: 'CD90', sub: 'identity', value: '95%', refText: '>= 95%', finding: 'pass', prov: 'measured' },
      { name: 'Endotoxin', sub: 'safety', value: '<0.5 EU', refText: '<0.5 EU', finding: 'pass', prov: 'reference' },
    ],
    recs: ['Compare against Lot 4471 and Lot 4455.', 'Capture reviewer note before any disposition workflow.'],
  },
  {
    lotId: '4455',
    risk: 'med',
    confidence: 0.74,
    summary: 'Variability review is open due to lower viability and passage band. Human reviewer action is required.',
    reviewedBasis: 'Release QC and cross-lot compare markers.',
    markers: [
      { name: 'Viability', sub: 'release', value: '92.8%', refText: '>= 85%', finding: 'warn', prov: 'measured' },
      { name: 'Passage', sub: 'expansion', value: 'P4', refText: 'P2-P4', finding: 'warn', prov: 'imported' },
      { name: 'CD45', sub: 'negative marker', value: '1.8%', refText: '<2%', finding: 'warn', prov: 'measured' },
      { name: 'Endotoxin', sub: 'safety', value: '<0.7 EU', refText: '<0.5 EU preferred', finding: 'warn', prov: 'reference' },
    ],
    recs: ['Escalate to QA reviewer queue.', 'Do not present outcome association as a claim.'],
  },
];

export const compareRows: Array<{ section?: string; key?: string; better?: 'high' | 'low' }> = [
  { section: 'Release characteristics' },
  { key: 'Viability', better: 'high' },
  { key: 'Passage', better: 'low' },
  { key: 'Endotoxin', better: 'low' },
  { section: 'Identity markers' },
  { key: 'CD73', better: 'high' },
  { key: 'CD90', better: 'high' },
  { key: 'CD45', better: 'low' },
];

export function lotById(id?: string | null) {
  return lots.find((lot) => lot.id === id || lot.line === id);
}

export function providerById(id?: string | null) {
  return providers.find((provider) => provider.id === id);
}

export function studyByCode(code?: string | null) {
  return studies.find((study) => study.code === code);
}

export function investigatorById(id?: string | null) {
  return investigators.find((investigator) => investigator.id === id);
}

export function signalById(id?: string | null) {
  return signals.find((signal) => signal.id === id);
}

export function patientById(id?: string | null) {
  return patients.find((patient) => patient.pid === id);
}

export function aeById(id?: string | null) {
  return adverseEvents.find((ae) => ae.id === id);
}

export function patientsByLot(id: string) {
  return patients.filter((patient) => patient.lot === id);
}

export function patientsByStudy(code: string) {
  return patients.filter((patient) => patient.study === code);
}

export function patientsByProvider(id: string) {
  return patients.filter((patient) => patient.provider === id);
}

export function studiesByInvestigator(id: string) {
  return studies.filter((study) => study.piId === id);
}

export function aesForPatient(pid: string) {
  return adverseEvents.filter((ae) => ae.pid === pid);
}

export function aesForLot(id: string) {
  return adverseEvents.filter((ae) => ae.lot === id);
}

export function panelFor(lotId: string): QcPanel {
  const lot = lotById(lotId);
  const viability = lot?.viability ?? '-';
  const passage = lot?.passage ?? '-';
  const endotoxin = lot?.endotoxin ?? '-';
  const viabilityValue = parseFloat(viability);
  const passageValue = parseFloat(passage.replace(/^P/i, ''));
  const endotoxinValue = parseFloat(endotoxin);
  const viabilityFinding = Number.isFinite(viabilityValue) ? (viabilityValue >= 85 ? 'pass' : 'fail') : 'unknown';
  const passageFinding = Number.isFinite(passageValue) ? (passageValue >= 2 && passageValue <= 4 ? 'pass' : 'fail') : 'unknown';
  const endotoxinFinding = Number.isFinite(endotoxinValue) ? (endotoxinValue < 0.5 ? 'pass' : 'fail') : 'unknown';

  return (
    qcPanels.find((panel) => panel.lotId === lotId) ?? {
      lotId,
      risk: 'low',
      confidence: 0.68,
      summary: 'No authored review panel for this lot. Show measured markers and require reviewer confirmation.',
      reviewedBasis: 'Fallback review prompt.',
      markers: [
        { name: 'Viability', sub: 'release', value: viability, refText: '>= 85%', finding: viabilityFinding, prov: 'measured' },
        { name: 'Passage', sub: 'expansion', value: passage, refText: 'P2-P4', finding: passageFinding, prov: 'imported' },
        { name: 'Endotoxin', sub: 'safety', value: endotoxin, refText: '<0.5 EU', finding: endotoxinFinding, prov: 'reference' },
      ],
      recs: ['Confirm against source QC packet.'],
    }
  );
}

export function coursesFor(patient: PatientRecord) {
  const lot = lotById(patient.lot);
  const multi = patient.pid === 'P-2030' || patient.pid === 'P-1991';
  const first = {
    i: 0,
    label: 'Course 01',
    kind: `${patient.indication} care loop`,
    lot: patient.lot,
    line: lot?.line,
    type: lot?.type,
    side: patient.side,
    injections: ['Dose 1', 'Dose 2'],
    reason: 'Initial care episode linked to source lot and follow-up plan.',
    peakLbl: String(Math.max(patient.idx - (multi ? 8 : 0), 48)),
    koos: patient.d,
    startDate: patient.enrolled,
    active: !multi,
    stageIdx: patient.stageIdx,
    color: 'var(--accent)',
  };
  if (!multi) {
    return { nCourses: 1, totalAdmins: first.injections.length, span: patient.days, lotSet: [patient.lot], courses: [first] };
  }
  const secondLot = patient.lot === '4455' ? '4482' : patient.lot;
  const second = {
    ...first,
    i: 1,
    label: 'Course 02',
    kind: patient.side === 'L' ? 'Contralateral loop' : 'Repeat follow-up loop',
    lot: secondLot,
    line: lotById(secondLot)?.line,
    type: lotById(secondLot)?.type,
    injections: ['Dose 1'],
    reason: 'Re-entered the closed loop after plateau review.',
    peakLbl: String(patient.idx),
    active: true,
    color: 'var(--violet)',
  };
  return {
    nCourses: 2,
    totalAdmins: first.injections.length + second.injections.length,
    span: patient.days,
    lotSet: Array.from(new Set([first.lot, second.lot])),
    courses: [{ ...first, active: false, stageIdx: 5 }, second],
  };
}
